import {
  AfterViewInit,
  Component,
  HostListener,
  OnDestroy,
  OnInit,
} from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { Subscription } from "rxjs";
import { ScanFilterStateService } from "src/app/services/scan-filter-state.service";
import { BreadcrumbService } from "src/app/services/breadcrumb.service";
import { DashboardService } from "src/app/services/home/dashboard.service";
import { ProductMasterService } from "src/app/services/masters/product-master.service";
import { ProductMasterResponseForDdl } from "src/app/models/Masters/productMaster";
import {
  apiErrorMessage,
  reportErrorState,
} from "src/app/shared/api-error.util";

const PRESET_LABELS: { [key: string]: string } = {
  today: "Today",
  yesterday: "Yesterday",
  last7: "Last 7 Days",
  last30: "Last 30 Days",
  thisMonth: "This Month",
  lastMonth: "Last Month",
  custom: "Custom Range",
};

interface KeyInsight {
  icon: string;
  iconBg: string;
  iconColor: string;
  label: string;
  value: string;
  subValue: string;
}

interface FilterChip {
  key: string;
  label: string;
  value: string;
}

/** A selectable product filter option: id is what actually goes into the
 *  API payload (ProductId), name is what's shown in the drawer's dropdown.
 *  Previously this was tracked as a bare product-name string with no id
 *  alongside it, which meant ProductId could never be populated in the
 *  request payload - see applyDrawerFilters()/getSelectedProductId(). */
interface ProductFilterOption {
  id: number | null;
  name: string;
}

interface ScanRecord {
  codeSerial: string;
  timestamp: string;
  productName: string;
  /** ASSUMPTION: the API row exposes a numeric product id under
   *  `productId` (see mapApiRow/mapScanMapApiRow). Confirm the actual
   *  field name against the real API response and adjust those two
   *  mappers if it differs (e.g. `prodId`, `productID`, or nested under
   *  a product object). */
  productId: number | null;
  mobileNumber: string;
  location: string;
  locationVerified: boolean;
  /** Shown verbatim, whatever the API sent (e.g. "Counterfeit") - the table
   *  used to force this into one of a fixed 4-value set and silently fall
   *  back to "Authentic" for anything else, which mislabeled a counterfeit
   *  scan as authentic. statusClass() below still recognizes those 4 for
   *  pill coloring and falls back to a neutral pill for everything else, so
   *  an unrecognized status just renders uncolored rather than wrong. */
  status: string;
  productRating: number | null;
  feedback: string;
  /** Why the scan was flagged (e.g. "Counterfeit", "Duplicate QR") - the
   *  API's free-text `remarks` field. */
  reason: string;
  /** How many times this same code has been scanned - null when the API
   *  doesn't send a count for this row (e.g. the scan-map endpoint). */
  scanCount: number | null;
  /** Raw postal address, separate from the combined `location` display
   *  string above. */
  address: string;
  city: string;
  state: string;
  country: string;
  /** Who verified/scanned this record - shown as "User Name" in the table. */
  verifiedBy: string;
  /** Scan coordinates - null when the API sent none (or unparsable ones). */
  latitude: number | null;
  longitude: number | null;
}

type ScanKpiType =
  | "total"
  | "authentic"
  | "suspected"
  | "duplicate"
  | "fake"
  | "unverified"
  | "location";

interface MetricConfig {
  breadcrumbLabel: string;
  pageTitle: string;
  infoTooltip: string;
  totalLabel: string;
  tableTitle: string;
  insightLabels: string[];
  sentiment: "up" | "warn" | "bad" | "neutral";
  accentColor: string | null;
  filterStatus: ScanRecord["status"] | null;
}

@Component({
  selector: "app-analytics",
  templateUrl: "./analytics.component.html",
  styleUrls: ["./analytics.component.css"],
})
export class AnalyticsComponent implements OnInit, AfterViewInit, OnDestroy {
  // ===================== Header / breadcrumb =====================
  breadcrumb = ["Dashboard", "Total Scans"];
  comparePeriodLabel = "";

  totalScans: number | null = null;
  totalScansChangePercent = 0;
  /** Parsed insights.trendGraph - see parseTrendGraph()/trendGraphPoints. */
  trendGraphData: number[] = [];
  metricType: ScanKpiType = "total";
  metricConfig: MetricConfig = AnalyticsComponent.METRIC_CONFIGS.total;

  keyInsights: KeyInsight[] = [];

  private static readonly INSIGHT_ICONS = [
    { icon: "fa fa-map-marker", iconBg: "#EEF2FF", iconColor: "#6366f1" },
    { icon: "fa fa-cube", iconBg: "#ECFDF5", iconColor: "#16a34a" },
    { icon: "fa fa-calendar", iconBg: "#FFF7ED", iconColor: "#f59e0b" },
    { icon: "fa fa-clock-o", iconBg: "#EFF6FF", iconColor: "#3b82f6" },
    { icon: "fa fa-line-chart", iconBg: "#EEF2FF", iconColor: "#6366f1" },
    { icon: "fa fa-users", iconBg: "#ECFDF5", iconColor: "#16a34a" },
  ];

  private static readonly METRIC_CONFIGS: Record<ScanKpiType, MetricConfig> = {
    total: {
      breadcrumbLabel: "Total Scans",
      pageTitle: "Total Scans",
      infoTooltip:
        "This metric shows the total number of times QR codes were scanned.",
      totalLabel: "Total Scans",
      tableTitle: "Scan Data",
      sentiment: "up",
      accentColor: null,
      filterStatus: null,
      insightLabels: [
        "Highest Scanning State",
        "Highest Scanning Product",
        "Peak Scan Day",
        "Peak Scan Hour",
        "Avg Scans / Day",
        "Total Customers",
      ],
    },
    authentic: {
      breadcrumbLabel: "Authentic Scans",
      pageTitle: "Authentic Scans",
      infoTooltip:
        "This metric shows scans of QR codes that were verified as genuine, authentic products.",
      totalLabel: "Total Authentic Scans",
      tableTitle: "Scan Data (Authentic Only)",
      sentiment: "up",
      accentColor: "#22c55e",
      filterStatus: "Authentic",
      insightLabels: [
        "Highest Scanning State",
        "Highest Scanning Product",
        "Peak Scan Day",
        "Peak Scan Hour",
        "Avg Scans / Day",
        "Total Customers",
      ],
    },
    suspected: {
      breadcrumbLabel: "Suspected Scans",
      pageTitle: "Suspected Scans",
      infoTooltip:
        "This metric shows scans flagged as suspected counterfeit or anomalous activity.",
      totalLabel: "Total Suspected Scans",
      tableTitle: "Scan Data (Suspected Only)",
      sentiment: "warn",
      accentColor: "#f59e0b",
      filterStatus: "Suspected",
      insightLabels: [
        "Highest Suspected State",
        "Highest Suspected Product",
        "Peak Suspicious Day",
        "Peak Suspicious Hour",
        "Avg Suspected / Day",
        "Total Customers",
      ],
    },
    duplicate: {
      breadcrumbLabel: "Duplicate Scans",
      pageTitle: "Duplicate Scans",
      infoTooltip:
        "This metric shows scans of QR codes that had already been scanned before.",
      totalLabel: "Total Duplicate Scans",
      tableTitle: "Scan Data (Duplicate Only)",
      sentiment: "bad",
      accentColor: "#ef4444",
      filterStatus: "Duplicate",
      insightLabels: [
        "Highest Duplicate State",
        "Most Duplicated Product",
        "Peak Duplicate Day",
        "Peak Duplicate Hour",
        "Avg Duplicate / Day",
        "Repeat Offender Codes",
      ],
    },
    fake: {
      breadcrumbLabel: "Fake Scans",
      pageTitle: "Fake Scans",
      infoTooltip:
        "This metric shows scans of QR codes identified as fake - not authentic or not issued by the HIL system.",
      totalLabel: "Total Fake Scans",
      tableTitle: "Scan Data (Fake Only)",
      sentiment: "bad",
      accentColor: "#ef4444",
      filterStatus: "Fake",
      insightLabels: [
        "Highest Fake State",
        // "Most Fake Product Code",
        "Peak Fake Day",
        "Peak Fake Hour",
        "Avg Fake / Day",
        // "Damaged/Tampered Codes",
      ],
    },
    unverified: {
      breadcrumbLabel: "Unverified Scans",
      pageTitle: "Unverified Scans",
      infoTooltip:
        "This metric shows scans that have not yet been checked against the verification database.",
      totalLabel: "Total Unverified Scans",
      tableTitle: "Scan Data (Unverified Only)",
      sentiment: "neutral",
      accentColor: "#8b5cf6",
      filterStatus: "Unverified",
      insightLabels: [
        "Highest Unverified State",
        // "Most Unverified Product Code",
        "Peak Unverified Day",
        "Peak Unverified Hour",
        "Avg Unverified / Day",
        "Damaged/Tampered Codes",
      ],
    },
    location: {
      breadcrumbLabel: "Scan Location Report",
      pageTitle: "India Scan & Risk Heatmap",
      infoTooltip:
        "This report shows every scan with its location - the same data behind the dashboard's India Scan & Risk Heatmap - so you can drill into scan density and risk hotspots.",
      totalLabel: "Total Scans",
      tableTitle: "Live Scan Feed",
      sentiment: "up",
      accentColor: null,
      filterStatus: null,
      insightLabels: [
        "Highest Risk State",
        "Highest Scan Density",
        "GPS-Verified Scans",
        "Active Hotspots",
        "Peak Scan Day",
        "Live Scan Rate",
      ],
    },
  };

  // ===================== "View details by" filters =====================
  viewDetailsByOptions = [
    "Code Wise",
    "Product Wise",
    "Location Wise",
    "Distributor Wise",
  ];
  viewDetailsBy = "Code Wise";

  filterChips: FilterChip[] = [
    { key: "region", label: "Region", value: "North India" },
    { key: "category", label: "Category", value: "Lubricants" },
    { key: "channel", label: "Channel", value: "Retailer" },
  ];

  // ===================== Table (API-backed) =====================
  tableTitle = "Scan Data";
  searchTerm = "";
  // 5 by default so the table + pagination fit without scrolling the page.
  rows = 5;
  rowsPerPageOptions = [5, 10, 25, 50, 100];
  currentPage = 1;
  loading = true;
  loadError = "";
  /** Set alongside loadError, drives the full-page ReportErrorStateComponent
   *  (see showFullPageError) - null while there's no error. */
  loadErrorState: { title: string; message: string } | null = null;
  scanData: ScanRecord[] = [];

  filteredScanData: ScanRecord[] = [];
  pagedScanData: ScanRecord[] = [];
  baseScanData: ScanRecord[] = this.scanData;
  usingServerData = false;
  private serverTotalRecords = 0;
  private serverTotalPages = 1;
  private serverPageRows: ScanRecord[] = [];
  isLiveFeed = false;
  visibleRowCount = Infinity;
  private liveRevealTimer: any = null;
  /** Debounces onSearch()'s server-side re-fetch so typing doesn't fire an
   *  API call on every keystroke - see onSearch(). */
  private searchDebounceTimer: any = null;
  private startLiveReveal(): void {
    if (this.liveRevealTimer) clearInterval(this.liveRevealTimer);
    this.visibleRowCount = 0;
    const total = this.pagedScanData.length;
    if (total === 0) return;
    this.liveRevealTimer = setInterval(() => {
      this.visibleRowCount++;
      if (this.visibleRowCount >= total) {
        clearInterval(this.liveRevealTimer);
        this.liveRevealTimer = null;
      }
    }, 350);
  }
  /** The drawer's Product dropdown options, `{id, name}` pairs so the
   *  payload can carry a real ProductId (see applyDrawerFilters()).
   *  Loaded once from the product master's DDL endpoint (loadFilterProducts,
   *  called from ngOnInit) - NOT built by scraping productName/productId off
   *  whichever page of scan rows happens to be loaded, which is what this
   *  used to do and is why the dropdown only ever showed products that had
   *  already appeared in a loaded page rather than the full catalog. */
  filterProductOptions: ProductFilterOption[] = [];
  filterStatusOptions: ScanRecord["status"][] = [
    "Authentic",
    "Suspected",
    "Duplicate",
    "Invalid",
    "Unverified",
  ];

  productSearchTerm = "";
  pendingProductFilter: ProductFilterOption | null = null;
  pendingStatusFilter: ScanRecord["status"] | null = null;
  appliedProductFilter: ProductFilterOption | null = null;
  appliedStatusFilter: ScanRecord["status"] | null = null;
  isFilterDrawerOpen = false;
  /** Which of the drawer's own Product/Scan Status dropdowns is open, or none. */
  openDropdown: "product" | "status" | null = null;
  /** True while exportData()'s "fetch everything" request is in flight -
   *  guards against a double-click firing two overlapping export calls,
   *  and drives the download button's spinner/disabled state. */
  isExporting = false;

  private static readonly THEME_EVENT = "sa-theme-change";
  isLightMode = document.body.classList.contains("light-mode");

  private filtersBtnClickHandler = (e: any): void => {
    if (
      e.target &&
      e.target.closest &&
      e.target.closest("#filtersTriggerBtn")
    ) {
      e.stopPropagation();
      this.openFilterDrawer();
    }
  };

  private themeChangeHandler = (): void => {
    this.isLightMode = document.body.classList.contains("light-mode");
  };

  // ===================== Export / Sync (shared navbar buttons) =====================
  private exportBtnClickHandler = (e: any): void => {
    if (e.target && e.target.closest && e.target.closest("#exportBtn")) {
      e.stopPropagation();
      this.exportData();
    }
  };

  private syncBtnClickHandler = (e: any): void => {
    if (e.target && e.target.closest && e.target.closest("#syncBtn")) {
      e.stopPropagation();
      this.refreshData();
    }
  };

  private dateRangeSub: Subscription | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private scanFilterState: ScanFilterStateService,
    public breadcrumbService: BreadcrumbService,
    private dashboardService: DashboardService,
    private productMasterService: ProductMasterService,
  ) {}

  /** Every crumb but the last points back to the dashboard - same reasoning
   *  as HeaderComponent.goToDashboard(), now that the breadcrumb is rendered
   *  in the page topbar instead of the navbar. */
  goToDashboard(): void {
    this.router.navigate(["/dashboard1"]);
  }

  ngOnInit(): void {
    const requestedType = (this.route.snapshot.paramMap.get("type") ||
      this.route.snapshot.data["type"] ||
      "total") as ScanKpiType;
    this.metricType = AnalyticsComponent.METRIC_CONFIGS[requestedType]
      ? requestedType
      : "total";
    this.metricConfig = AnalyticsComponent.METRIC_CONFIGS[this.metricType];
    this.isLiveFeed = this.metricType === "location";

    this.breadcrumb = ["Dashboard", this.metricConfig.breadcrumbLabel];
    this.tableTitle = this.metricConfig.tableTitle;
    // Renders as 6 skeleton insight cards until the API response fills in
    // real values (see mapApiInsights) - no mock numbers here anymore.
    this.keyInsights = this.buildSkeletonInsights();
    this.baseScanData = this.metricConfig.filterStatus
      ? this.scanData.filter(
          (row) => row.status === this.metricConfig.filterStatus,
        )
      : this.scanData;

    // Rendered in this page's own topbar (az-breadcrumb) - see BreadcrumbService.
    this.breadcrumbService.items = this.breadcrumb;
    // scanFilterState.product now needs to carry enough to rebuild a
    // ProductFilterOption on load (previously just a name string). See
    // ScanFilterStateService - if it still only stores `product`/`productId`
    // as separate fields, that's fine; we just recombine them here.
    const sharedProductId = this.getSharedProductId();
    const sharedProductName = this.scanFilterState.product;
    this.appliedProductFilter =
      sharedProductName || sharedProductId != null
        ? {
            id: sharedProductId,
            name: sharedProductName || `Product ${sharedProductId}`,
          }
        : null;
    this.appliedStatusFilter = this.metricConfig.filterStatus
      ? null
      : (this.scanFilterState.status as ScanRecord["status"] | null);

    this.currentPage = 1;
    this.loading = true;

    this.loadFilterProducts();

    let receivedInitialEmission = false;
    this.dateRangeSub = this.scanFilterState.dateRange$.subscribe(() => {
      receivedInitialEmission = true;
      this.currentPage = 1;
      this.loadReport();
    });
    if (!receivedInitialEmission) {
      this.loadReport();
    }
  }

  /** Populates filterProductOptions from the product master's own DDL
   *  endpoint - the full, active product catalog for the company - rather
   *  than from whatever products happen to show up in the currently loaded
   *  page of scan data (see filterProductOptions' doc comment). pageSize:
   *  -1 asks for every active product in one response, same convention
   *  exportData() uses for "every row" against the scan APIs. */
  private loadFilterProducts(): void {
    const raw = localStorage.getItem("user");
    const compId = raw ? JSON.parse(raw).compId : null;
    if (!compId) return;

    this.productMasterService
      .loadProductsForDdl({
        pageSize: -1,
        isActive: "1",
        compId,
        searchValue: "",
      })
      .subscribe({
        next: (response: ProductMasterResponseForDdl) => {
          if (response.responseCode === "200" && response.details) {
            this.filterProductOptions = response.details
              .map((p) => ({ id: p.productId, name: p.productNm }))
              .sort((a, b) => a.name.localeCompare(b.name));
          }
        },
        error: (error) => {
          console.error("GetProductsForDdl error:", error);
        },
      });
  }

  ngAfterViewInit(): void {
    document.addEventListener("click", this.filtersBtnClickHandler);
    document.addEventListener("click", this.exportBtnClickHandler);
    document.addEventListener("click", this.syncBtnClickHandler);
    window.addEventListener(
      AnalyticsComponent.THEME_EVENT,
      this.themeChangeHandler,
    );
    this.updateHeaderFiltersBadge();
  }

  ngOnDestroy(): void {
    if (this.dateRangeSub) this.dateRangeSub.unsubscribe();
    if (this.liveRevealTimer) clearInterval(this.liveRevealTimer);
    if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
    document.removeEventListener("click", this.filtersBtnClickHandler);
    document.removeEventListener("click", this.exportBtnClickHandler);
    document.removeEventListener("click", this.syncBtnClickHandler);
    window.removeEventListener(
      AnalyticsComponent.THEME_EVENT,
      this.themeChangeHandler,
    );
    // So a later page with no breadcrumb of its own doesn't inherit this one.
    this.breadcrumbService.items = [];
  }

  @HostListener("document:click")
  onDocumentClick(): void {
    this.openDropdown = null;
  }

  toggleDropdown(which: "product" | "status", ev: Event): void {
    ev.stopPropagation();
    this.openDropdown = this.openDropdown === which ? null : which;
  }

  /** Search relies entirely on the backend: the debounced call below sends
   *  `searchTerm` as the API's Search param and the server-paginated
   *  response (serverPageRows/serverTotalRecords/serverTotalPages) fully
   *  drives the table - there is no client-side filtering here. Debounced
   *  so a re-fetch only fires once the user pauses typing, not on every
   *  keystroke. */
  onSearch(term: string): void {
    this.searchTerm = term;

    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }

    this.searchDebounceTimer = setTimeout(() => {
      this.currentPage = 1;
      this.loadReport();
    }, 400);
  }
  /** "YYYY-MM-DD" in local time (not toISOString, which shifts to UTC and
   *  can land on the wrong day depending on the user's timezone). */
  private formatIsoDate(d: Date): string {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  private getPresetDateRange(range: string | null): {
    from: string;
    to: string;
  } {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    switch (range) {
      case "today": {
        return {
          from: this.formatIsoDate(today),
          to: this.formatIsoDate(today),
        };
      }
      case "yesterday": {
        const d = new Date(today);
        d.setDate(d.getDate() - 1);
        return { from: this.formatIsoDate(d), to: this.formatIsoDate(d) };
      }
      case "last7": {
        const from = new Date(today);
        from.setDate(from.getDate() - 6);
        return {
          from: this.formatIsoDate(from),
          to: this.formatIsoDate(today),
        };
      }
      case "thisMonth": {
        const from = new Date(today.getFullYear(), today.getMonth(), 1);
        return {
          from: this.formatIsoDate(from),
          to: this.formatIsoDate(today),
        };
      }
      case "lastMonth": {
        const lastOfPrevMonth = new Date(
          today.getFullYear(),
          today.getMonth(),
          0,
        );
        const firstOfPrevMonth = new Date(
          lastOfPrevMonth.getFullYear(),
          lastOfPrevMonth.getMonth(),
          1,
        );
        return {
          from: this.formatIsoDate(firstOfPrevMonth),
          to: this.formatIsoDate(lastOfPrevMonth),
        };
      }
      case "last30":
      default: {
        const from = new Date(today);
        from.setDate(from.getDate() - 29);
        return {
          from: this.formatIsoDate(from),
          to: this.formatIsoDate(today),
        };
      }
    }
  }

  private getDateRange(): { from: string; to: string } {
    const range = this.scanFilterState.dateRangeSnapshot;

    return {
      from: range.dateFrom || "",
      to: range.dateTo || "",
    };
  }

  private getCompId(): number {
    const raw = localStorage.getItem("user");
    const compId = raw ? JSON.parse(raw).compId : null;
    return compId || 1;
  }

  /** 6 placeholder Key Insight cards (icon set + label, blank value/subValue)
   *  shown while loading is true / before the first API response arrives,
   *  so the template can render its skeleton shimmer over `value`/`subValue`
   *  without needing a separate "insights loading" branch. */
  private buildSkeletonInsights(): KeyInsight[] {
    return this.metricConfig.insightLabels.map((label, i) => ({
      ...AnalyticsComponent.INSIGHT_ICONS[i],
      label,
      value: "",
      subValue: "",
    }));
  }

  private applyAllFilters(): void {
    if (this.usingServerData) {
      this.pagedScanData = this.serverPageRows;

      if (this.isLiveFeed) {
        this.startLiveReveal();
      }

      return;
    }

    this.filteredScanData = this.baseScanData;
    this.currentPage = 1;
    this.updatePagedData();
  }

  private updateHeaderFiltersBadge(): void {
    const countEl = document.getElementById("filtersCount");
    const btn = document.getElementById("filtersTriggerBtn");
    if (!countEl || !btn) return;
    const active = [this.appliedProductFilter, this.appliedStatusFilter].filter(
      Boolean,
    ).length;
    countEl.textContent = String(active);
    btn.classList.toggle("has-active", active > 0);
  }
  private getSelectedStatus(): string | null {
    if (this.metricConfig.filterStatus) {
      return this.metricConfig.filterStatus;
    }

    return this.scanFilterState.status;
  }
  /**
   * Returns the ProductId that must be sent to the analytics APIs.
   *
   * The navbar/global filter stores the selected product as a numeric
   * `productId`. Older filter-state code can also leave the selected value in
   * `product` as a numeric string, so keep that as a safe fallback. The local
   * Analytics drawer stores the complete { id, name } option, so that is the
   * final fallback when the shared state has not been populated yet.
   */
  private getSharedProductId(): number | null {
    const sharedProductId = this.scanFilterState.productId;
    if (sharedProductId !== undefined && sharedProductId !== null) {
      const id = Number(sharedProductId);
      return Number.isFinite(id) ? id : null;
    }

    const sharedProduct = this.scanFilterState.product;
    if (sharedProduct !== undefined && sharedProduct !== null) {
      const id = Number(sharedProduct);
      if (Number.isFinite(id) && id > 0) {
        return id;
      }
    }

    return null;
  }

  private getSelectedProductId(): number | null {
    const sharedProductId = this.scanFilterState.productId;

    if (sharedProductId !== undefined && sharedProductId !== null) {
      const id = Number(sharedProductId);
      return Number.isFinite(id) ? id : null;
    }

    // Backward-compatible fallback for global/header filter state where
    // `product` may contain the ProductId as a string.
    const sharedProduct = this.scanFilterState.product;
    if (sharedProduct !== undefined && sharedProduct !== null) {
      const id = Number(sharedProduct);
      if (Number.isFinite(id) && id > 0) {
        return id;
      }
    }

    // Local Analytics drawer fallback.
    const localProductId = this.appliedProductFilter
      ? this.appliedProductFilter.id
      : null;
    if (localProductId !== undefined && localProductId !== null) {
      const id = Number(localProductId);
      return Number.isFinite(id) ? id : null;
    }

    return null;
  }

  /** "Jun 28, 2025" - date only, for peak-scan-day and the compare-period
   *  label. Falls back to the raw string if it isn't a parseable date. */
  private formatDateOnly(iso: string | null | undefined): string {
    if (!iso) return "-";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return String(iso);
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }

  /** "Jul 9, 2025 10:15 AM" - matches the table's existing timestamp format. */
  private formatScanTimestamp(iso: string | null | undefined): string {
    if (!iso) return "-";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return String(iso);
    const datePart = d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
    const timePart = d.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
    return `${datePart} ${timePart}`;
  }

  /** "6.20Cr" / "4.10L" / "612.00K" / "33" - matches the compact style the
   *  Key Insights panel expects its subValues in. Indian numbering
   *  (lakh/crore), not the M/K short-scale — 1L = 1,00,000, 1Cr =
   *  1,00,00,000. Always 2 decimal places once a suffix applies, so values
   *  stay visually aligned instead of some showing "4L" and others "4.10L".
   *  Public so the Total Scans KPI value in the template can reuse it too. */
  formatCompactNumber(n: number): string {
    if (n >= 10000000) return (n / 10000000).toFixed(2) + "Cr";
    if (n >= 100000) return (n / 100000).toFixed(2) + "L";
    if (n >= 1000) return (n / 1000).toFixed(2) + "K";
    return String(n);
  }

  /** Parses insights.trendGraph, which arrives as a JSON-encoded number
   *  array string (e.g. "[0,0,0,0,0,0,0,0,0,0,0,62]"), into a plain
   *  number[]. Falls back to an empty array on malformed/missing data so
   *  the sparkline just renders nothing instead of throwing. */
  private parseTrendGraph(raw: unknown): number[] {
    if (typeof raw !== "string" || !raw.trim()) return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed)
        ? parsed.filter((n) => typeof n === "number" && !isNaN(n))
        : [];
    } catch {
      return [];
    }
  }

  private mapApiRow(row: any): ScanRecord {
    const locationParts = [row.city, row.state, row.country].filter(
      (part: string | null) => !!part,
    );
    const location =
      locationParts.length > 0
        ? locationParts.join(", ")
        : row.userAddress || "-";
    const status: ScanRecord["status"] = row.status || "-";

    return {
      codeSerial: row.codeSerial != null ? String(row.codeSerial) : "-",
      timestamp: this.formatScanTimestamp(row.scanDate),
      productName: row.productNm || "-",
      // ASSUMPTION: field name - confirm against the real API response.
      productId: row.productId != null ? Number(row.productId) : null,
      mobileNumber: row.mobileNumber || "-",
      location,
      locationVerified: !!(row.city || row.state || row.userAddress),
      status,
      productRating:
        row.productRating != null ? Number(row.productRating) : null,
      feedback: row.usrFeedback || "",
      reason: row.remarks || "-",
      scanCount: row.scanCount != null ? Number(row.scanCount) : null,
      address: row.userAddress || "-",
      city: row.city || "-",
      state: row.state || "-",
      country: row.country || "-",
      verifiedBy: row.verifiedBy || "-",
      latitude: this.toCoord(row.latitude),
      longitude: this.toCoord(row.longitude),
    };
  }
  private mapScanMapApiRow(row: any): ScanRecord {
    const locationParts = [row.usrCity, row.usrState, row.country].filter(
      (part: string | null) => !!part,
    );

    const location =
      locationParts.length > 0 ? locationParts.join(", ") : row.address || "-";

    const status: ScanRecord["status"] = row.status || "-";

    return {
      codeSerial: row.codeSerial != null ? String(row.codeSerial) : "-",

      timestamp: this.formatScanTimestamp(row.timeStamp),

      productName: row.productName || "-",

      // ASSUMPTION: field name - confirm against the real API response.
      productId: row.productId != null ? Number(row.productId) : null,

      mobileNumber: row.mobileNumber || "-",

      location,

      locationVerified: !!(
        row.usrCity ||
        row.usrState ||
        row.address ||
        (row.usrLat != null && row.usrLng != null)
      ),

      status,

      productRating:
        row.productRating != null ? Number(row.productRating) : null,

      feedback: row.feedback || "",

      // This endpoint (the "location" KPI's scan map, not the other 6
      // KPIs' getScanIntelligenceGraph) doesn't send a reason/scan-count/
      // verifiedBy per row - "-"/null render the same as any other row
      // with nothing to show, rather than a blank cell.
      reason: row.remarks || "-",
      scanCount: row.scanCount != null ? Number(row.scanCount) : null,
      address: row.address || "-",
      city: row.usrCity || "-",
      state: row.usrState || "-",
      country: row.country || "-",
      verifiedBy: row.verifiedBy || "-",
      latitude: this.toCoord(row.latitude != null ? row.latitude : row.usrLat),
      longitude: this.toCoord(
        row.longitude != null ? row.longitude : row.usrLng,
      ),
    };
  }

  /** The API sends coordinates as strings (e.g. "29.39086"); blank/0/NaN
   *  count as "no location". */
  private toCoord(v: any): number | null {
    if (v == null || v === "") return null;
    const n = Number(v);
    return isFinite(n) && n !== 0 ? n : null;
  }

  hasCoords(row: ScanRecord): boolean {
    return row.latitude != null && row.longitude != null;
  }

  coordLabel(row: ScanRecord): string {
    return this.hasCoords(row) ? `${row.latitude}, ${row.longitude}` : "-";
  }

  /** Google Maps pinned at the scan's own coordinates. */
  mapUrl(row: ScanRecord): string {
    return `https://www.google.com/maps?q=${row.latitude},${row.longitude}`;
  }
  private mapApiInsights(insights: any): KeyInsight[] {
    const labels = this.metricConfig.insightLabels;
    const skipProductInsight =
      this.metricType === "fake" || this.metricType === "unverified";
    const values: { value: string; subValue: string }[] = [
      {
        value: insights.highestScanningState || "-",
        subValue:
          insights.highestScanningStateCount != null
            ? `${this.formatCompactNumber(insights.highestScanningStateCount)} (${insights.highestScanningStatePct}%)`
            : "-",
      },
      ...(skipProductInsight
        ? []
        : [
            {
              value: insights.highestScanningProduct || "-",
              subValue:
                insights.highestScanningProductCount != null
                  ? `${this.formatCompactNumber(insights.highestScanningProductCount)} (${insights.highestScanningProductPct}%)`
                  : "-",
            },
          ]),
      {
        value: this.formatDateOnly(insights.peakScanDay),
        subValue:
          insights.peakScanDayCount != null
            ? `${this.formatCompactNumber(insights.peakScanDayCount)} Scans`
            : "-",
      },
      {
        value: insights.peakScanHourLabel || "-",
        subValue:
          insights.peakScanHourCount != null
            ? `${this.formatCompactNumber(insights.peakScanHourCount)} Scans`
            : "-",
      },
      {
        value:
          insights.avgScansPerDay != null
            ? this.formatCompactNumber(insights.avgScansPerDay)
            : "-",
        subValue: "Scans",
      },
      this.metricType === "duplicate"
        ? {
            value:
              insights.repeatOffenderCodes != null
                ? this.formatCompactNumber(insights.repeatOffenderCodes)
                : "-",
            subValue: "Codes",
          }
        : this.metricType === "fake"
          ? {
              // "Damaged/Tampered Codes" label is commented out in
              // METRIC_CONFIGS, so this value is unused
              value: "-",
              // insights.damagedTamperedCodes != null
              //   ? this.formatCompactNumber(insights.damagedTamperedCodes)
              //   : "-",
              subValue: "Codes",
            }
          : this.metricType === "unverified"
            ? {
                value:
                  insights.pendingVerificationCodes != null
                    ? this.formatCompactNumber(
                        insights.pendingVerificationCodes,
                      )
                    : "-",
                subValue: "Codes",
              }
            : {
                value:
                  insights.uniqueConsumers != null
                    ? this.formatCompactNumber(insights.uniqueConsumers)
                    : "-",
                subValue: `(${this.scanFilterState.datePreset || "Custom Range"})`,
              },
    ];

    return labels.map((label, i) => ({
      ...AnalyticsComponent.INSIGHT_ICONS[i],
      label,
      value: values[i].value,
      subValue: values[i].subValue,
    }));
  }

  private getScanMapReport(): void {
    const { from, to } = this.getDateRange();

    const payload = {
      CompId: this.getCompId(),
      FromDt: from,
      ToDt: to,
      PageNo: this.currentPage,
      PageSize: Number(this.rows),
      ProductId: this.getSelectedProductId(),
      Status: this.getSelectedStatus(),
      Search: this.searchTerm.trim() || null,
    };

    this.loading = true;
    this.loadError = "";
    this.loadErrorState = null;

    this.dashboardService.getScanMapReport(payload).subscribe({
      next: (response: any) => {
        this.loading = false;

        const details = response && response.details;

        if (!details) {
          // The backend can report a failure (e.g. a DB timeout) as an HTTP
          // 200 with an error body instead of a real HTTP error status, so
          // this "no details" case must still surface the full-page error
          // state - not just the inline `loadError` string, which nothing
          // renders on its own.
          const wrapped = { error: response };
          this.loadError = apiErrorMessage(wrapped);
          this.loadErrorState = reportErrorState(wrapped);
          return;
        }

        // --------------------------------------------------
        // TABLE DATA
        // --------------------------------------------------
        const apiRows: any[] = Array.isArray(details.scanData)
          ? details.scanData
          : [];

        this.serverPageRows = apiRows.map((row) => this.mapScanMapApiRow(row));
        this.usingServerData = true;

        // --------------------------------------------------
        // PAGINATION
        // --------------------------------------------------
        const pagination = details.paginationInfo;

        if (pagination) {
          this.serverTotalRecords =
            typeof pagination.totalRecords === "number"
              ? pagination.totalRecords
              : this.serverPageRows.length;

          this.serverTotalPages =
            typeof pagination.totalPages === "number"
              ? pagination.totalPages
              : Math.max(1, Math.ceil(this.serverTotalRecords / this.rows));

          if (typeof pagination.currentPage === "number") {
            this.currentPage = pagination.currentPage;
          }

          if (
            typeof pagination.pageSize === "number" &&
            pagination.pageSize > 0
          ) {
            this.rows = pagination.pageSize;
          }
        } else {
          this.serverTotalRecords = this.serverPageRows.length;
          this.serverTotalPages = Math.max(
            1,
            Math.ceil(this.serverTotalRecords / this.rows),
          );
        }

        // --------------------------------------------------
        // DASHBOARD METRICS / KPIs
        // --------------------------------------------------
        const metrics = details.dashboardMetrics;

        if (metrics) {
          if (typeof metrics.totalScans === "number") {
            this.totalScans = metrics.totalScans;
          }

          if (typeof metrics.totalScansChangePct === "number") {
            this.totalScansChangePercent = metrics.totalScansChangePct;
          }

          // Previous comparison period
          if (metrics.previousPeriodStart && metrics.previousPeriodEnd) {
            this.comparePeriodLabel = `${this.formatDateOnly(
              metrics.previousPeriodStart,
            )} - ${this.formatDateOnly(metrics.previousPeriodEnd)}`;
          }

          // Location-specific insights
          this.keyInsights = this.mapScanMapInsights(metrics);
        }

        // --------------------------------------------------
        // GRAPH
        // --------------------------------------------------
        const graph = details.dashboardGraph;

        if (graph) {
          this.trendGraphData = this.parseTrendGraph(graph.totalScanGraph);
        }

        this.applyAllFilters();
      },

      error: (error) => {
        console.error("GetScanMapReport error:", error);

        this.loading = false;
        this.loadError = apiErrorMessage(error);
        this.loadErrorState = reportErrorState(error);
      },
    });
  }
  private mapScanMapInsights(metrics: any): any[] {
    const values = [
      {
        value: metrics.highestRiskState || "-",
        subValue:
          metrics.highestRiskStateCount != null
            ? `${metrics.highestRiskStateCount} scans (${
                metrics.highestRiskStatePct != null
                  ? metrics.highestRiskStatePct
                  : 0
              }%)`
            : "-",
      },
      {
        value: metrics.highestDensityCity || "-",
        subValue:
          metrics.highestDensityCityCount != null
            ? `${metrics.highestDensityCityCount} scans (${
                metrics.highestDensityCityPct != null
                  ? metrics.highestDensityCityPct
                  : 0
              }%)`
            : "-",
      },
      {
        value:
          metrics.gpsVerifiedScans != null
            ? this.formatCompactNumber(metrics.gpsVerifiedScans)
            : "-",
        subValue:
          metrics.gpsVerifiedPct != null ? `${metrics.gpsVerifiedPct}%` : "-",
      },
      {
        value:
          metrics.activeHotspots != null
            ? this.formatCompactNumber(metrics.activeHotspots)
            : "-",
        subValue: "Zones flagged",
      },
      {
        value: metrics.peakDay ? this.formatDateOnly(metrics.peakDay) : "-",
        subValue:
          metrics.peakDayCount != null ? `${metrics.peakDayCount} scans` : "-",
      },
      {
        value: metrics.liveScanRate != null ? `${metrics.liveScanRate}` : "-",
        subValue: "scans/min",
      },
    ];

    const labels = [
      "Highest Risk State",
      "Highest Scan Density",
      "GPS-Verified Scans",
      "Active Hotspots",
      "Peak Scan Day",
      "Live Scan Rate",
    ];

    return labels.map((label, i) => ({
      ...AnalyticsComponent.INSIGHT_ICONS[i],
      label,
      value: values[i].value,
      subValue: values[i].subValue,
    }));
  }
  private getScanIntelligenceGraph(): void {
    const { from, to } = this.getDateRange();
    const payload = {
      CompId: this.getCompId(),
      FromDate: from,
      ToDate: to,
      PageNo: this.currentPage,
      PageSize: Number(this.rows),
      ProductId: this.getSelectedProductId(),
      Status: this.getSelectedStatus(),
      Search: this.searchTerm.trim() || null,
    };

    this.loading = true;
    this.loadError = "";
    this.loadErrorState = null;

    this.dashboardService.getScanIntelligenceGraph(payload).subscribe({
      next: (response: any) => {
        this.loading = false;

        const details = response && response.details;
        if (!details) {
          // See the identical comment in getScanMapReport(): a body-level
          // failure still comes back as HTTP 200 here, so it has to be
          // routed through the same full-page error state as a real
          // HTTP error, not just the inline `loadError` string.
          const wrapped = { error: response };
          this.loadError = apiErrorMessage(wrapped);
          this.loadErrorState = reportErrorState(wrapped);
          return;
        }

        const apiRows: any[] = Array.isArray(details.data) ? details.data : [];
        this.serverPageRows = apiRows.map((row) => this.mapApiRow(row));
        this.usingServerData = true;

        this.serverTotalRecords =
          typeof details.totalRows === "number"
            ? details.totalRows
            : this.serverPageRows.length;
        this.serverTotalPages =
          typeof details.totalPages === "number"
            ? details.totalPages
            : Math.max(1, Math.ceil(this.serverTotalRecords / this.rows));
        if (typeof details.pageNo === "number") {
          this.currentPage = details.pageNo;
        }
        if (typeof details.pageSize === "number" && details.pageSize > 0) {
          this.rows = details.pageSize;
        }

        const insights = details.insights;
        if (insights) {
          if (typeof insights.totalScans === "number") {
            this.totalScans = insights.totalScans;
          }
          if (typeof insights.pctChangeVsPrevPeriod === "number") {
            this.totalScansChangePercent = insights.pctChangeVsPrevPeriod;
          }
          this.trendGraphData = this.parseTrendGraph(insights.trendGraph);
          if (insights.prevPeriodStart && insights.prevPeriodEnd) {
            this.comparePeriodLabel = `${this.formatDateOnly(
              insights.prevPeriodStart,
            )} - ${this.formatDateOnly(insights.prevPeriodEnd)}`;
          }
          this.keyInsights = this.mapApiInsights(insights);
        }

        this.applyAllFilters();
      },
      error: (error) => {
        console.error("GetScanIntelligenceGraph error:", error);
        this.loading = false;
        // No local fallback data anymore - surface the failure with the
        // full-page report error state instead of leaving stale/empty
        // content on screen, even if a page had already loaded before.
        this.loadError = apiErrorMessage(error);
        this.loadErrorState = reportErrorState(error);
      },
    });
  }
  /** filterProductOptions narrowed by productSearchTerm - same search-as-you-
   *  type behavior as dashboard-new.component.ts's filterProducts(), just as
   *  a template-driven getter instead of that component's raw-DOM sync. */
  get visibleProductOptions(): ProductFilterOption[] {
    const query = this.productSearchTerm.trim().toLowerCase();
    if (!query) return this.filterProductOptions;
    return this.filterProductOptions.filter((p) =>
      p.name.toLowerCase().includes(query),
    );
  }

  openFilterDrawer(): void {
    this.pendingProductFilter = this.appliedProductFilter;
    this.pendingStatusFilter = this.appliedStatusFilter;
    this.productSearchTerm = "";
    this.isFilterDrawerOpen = true;
  }

  closeFilterDrawer(): void {
    this.isFilterDrawerOpen = false;
  }

  applyDrawerFilters(): void {
    this.appliedProductFilter = this.pendingProductFilter;
    this.appliedStatusFilter = this.pendingStatusFilter;
    this.scanFilterState.product = this.appliedProductFilter
      ? this.appliedProductFilter.name
      : null;
    // This was the missing piece: ProductId never got written into
    // scanFilterState, so getSelectedProductId() (and therefore the
    // request payload) always saw whatever stale/null value was already
    // there.
    this.scanFilterState.productId = this.appliedProductFilter
      ? this.appliedProductFilter.id
      : null;
    this.scanFilterState.status = this.appliedStatusFilter;
    this.applyAllFilters();
    this.updateHeaderFiltersBadge();
    this.closeFilterDrawer();
    // Product/status changed what the server-side query should return too.
    this.currentPage = 1;
    this.loadReport();
  }

  clearDrawerFilters(): void {
    this.pendingProductFilter = null;
    this.pendingStatusFilter = null;
    this.appliedProductFilter = null;
    this.appliedStatusFilter = null;
    this.scanFilterState.product = null;
    this.scanFilterState.productId = null;
    this.scanFilterState.status = null;
    this.applyAllFilters();
    this.updateHeaderFiltersBadge();
    this.closeFilterDrawer();
    this.currentPage = 1;
    this.loadReport();
  }

  get totalRecords(): number {
    return this.usingServerData
      ? this.serverTotalRecords
      : this.filteredScanData.length;
  }

  get totalPages(): number {
    return this.usingServerData
      ? Math.max(1, this.serverTotalPages)
      : Math.max(1, Math.ceil(this.totalRecords / this.rows));
  }

  get rangeStart(): number {
    return this.totalRecords === 0 ? 0 : (this.currentPage - 1) * this.rows + 1;
  }

  get rangeEnd(): number {
    return Math.min(this.currentPage * this.rows, this.totalRecords);
  }

  get pageNumbers(): (number | "...")[] {
    const total = this.totalPages;
    const current = this.currentPage;
    const pages: (number | "...")[] = [];
    const windowSize = 1;

    pages.push(1);
    if (current - windowSize > 2) {
      pages.push("...");
    }
    for (
      let p = Math.max(2, current - windowSize);
      p <= Math.min(total - 1, current + windowSize);
      p++
    ) {
      pages.push(p);
    }
    if (current + windowSize < total - 1) {
      pages.push("...");
    }
    if (total > 1) {
      pages.push(total);
    }
    return pages;
  }

  /** How many skeleton rows the template should render while `loading` is
   *  true and no data has loaded yet - matches the current page size so
   *  the skeleton doesn't jump around once real rows arrive. */
  get skeletonRows(): number[] {
    return Array(this.rows)
      .fill(0)
      .map((_, i) => i);
  }

  /** True while the table body should show its skeleton shimmer instead of
   *  real rows or an empty state - i.e. still loading and nothing has ever
   *  loaded successfully yet. A refresh/pagination call after data is
   *  already on screen sets `loading` too, but the template should keep
   *  showing the existing rows during that refetch rather than replacing
   *  them with skeletons - use this getter rather than `loading` directly. */
  get showTableSkeleton(): boolean {
    return this.loading && !this.usingServerData;
  }

  /** True whenever the latest load attempt failed - shows the full-page
   *  ReportErrorStateComponent in place of the report content, even if a
   *  previous page of data is still sitting in memory. */
  get showFullPageError(): boolean {
    return !!this.loadErrorState;
  }

  /** Same idea as showTableSkeleton, for the Key Insight cards and the
   *  Total Scans figure. */
  get showInsightsSkeleton(): boolean {
    return this.loading && this.totalScans === null;
  }

  /** Builds the sparkline's line + area path data from trendGraphData,
   *  scaled into the SVG's existing 0 0 300 90 viewBox (see
   *  .az-total-scans-spark in analytics.component.html). Points use plain
   *  L segments (straight lines) rather than a smoothed curve - trend
   *  buckets are discrete periods (e.g. hours/days), so a straight
   *  connector between them is honest about the data rather than implying
   *  interpolated values in between. 10px of vertical padding top/bottom
   *  keeps the line off the card's edges even at the data's min/max. */
  private buildTrendPaths(): { line: string; area: string } {
    const data = this.trendGraphData;
    if (data.length < 2) {
      return { line: "", area: "" };
    }

    const width = 300;
    const height = 90;
    const padTop = 10;
    const padBottom = 10;
    const usableHeight = height - padTop - padBottom;
    const max = Math.max(...data);
    const min = Math.min(...data);
    const range = max - min || 1;
    // Baseline padding: without this, a value equal to `min` (e.g. the
    // string of zeros before a late spike, as in "[0,0,...,0,62]") maps
    // to y = height exactly and the line hugs the card's bottom edge
    // closely enough to read as invisible/missing rather than flat-zero.
    // Extending the effective range 10% below the real minimum lifts that
    // flat stretch just clear of the edge while barely affecting where
    // the actual max sits.
    const baselinePad = range * 0.1;
    const paddedRange = range + baselinePad;
    const stepX = width / (data.length - 1);

    const points = data.map((v, i) => ({
      x: i * stepX,
      y:
        padTop +
        usableHeight -
        ((v - min + baselinePad) / paddedRange) * usableHeight,
    }));

    const line = points
      .map(
        (p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`,
      )
      .join(" ");
    const last = points[points.length - 1];
    const first = points[0];
    const area = `${line} L${last.x.toFixed(1)},${height} L${first.x.toFixed(1)},${height} Z`;

    return { line, area };
  }

  /** [attr.d] for .az-total-scans-spark's stroked line path. */
  get trendLinePath(): string {
    return this.buildTrendPaths().line;
  }

  /** [attr.d] for .az-total-scans-spark's filled gradient area path. */
  get trendAreaPath(): string {
    return this.buildTrendPaths().area;
  }

  updatePagedData(): void {
    if (this.usingServerData) {
      // pagedScanData is already the server's current page (set in
      // getScanIntelligenceGraph/applyAllFilters) - every pagination
      // handler below calls getScanIntelligenceGraph() right after this,
      // which refreshes it once the new page arrives.
      if (this.isLiveFeed) {
        this.startLiveReveal();
      }
      return;
    }
    const start = (this.currentPage - 1) * this.rows;
    this.pagedScanData = this.filteredScanData.slice(start, start + this.rows);
    if (this.isLiveFeed) {
      this.startLiveReveal();
    }
  }
  private loadReport(): void {
    if (this.metricType === "location") {
      this.getScanMapReport();
    } else {
      this.getScanIntelligenceGraph();
    }
  }
  goToPage(page: number | "..."): void {
    if (page === "..." || page === this.currentPage) {
      return;
    }
    this.currentPage = page;
    this.updatePagedData();
    this.loadReport();
  }

  prevPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.updatePagedData();
      this.loadReport();
    }
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.updatePagedData();
      this.loadReport();
    }
  }

  onRowsChange(rows: number): void {
    this.rows = rows;
    this.currentPage = 1;
    this.updatePagedData();
    this.loadReport();
  }

  removeFilterChip(key: string): void {
    this.filterChips = this.filterChips.filter((chip) => chip.key !== key);
  }

  clearAllFilters(): void {
    this.filterChips = [];
  }

  /** "az-up" / "az-warn" / "az-bad" - drives the compare-row and change-pill
   *  color (see METRIC_CONFIGS[...].sentiment and analytics.component.css). */
  get sentimentClass(): string {
    return "az-" + this.metricConfig.sentiment;
  }

  /** "fa-arrow-up" / "fa-arrow-down" - the API's pctChangeVsPrevPeriod can
   *  be negative (see sample response), so the arrow direction follows the
   *  actual sign rather than always pointing up. */
  get changeArrowIcon(): string {
    return this.totalScansChangePercent < 0 ? "fa-arrow-down" : "fa-arrow-up";
  }

  /** "az-up" / "az-bad" for the change pill specifically (e.g. "-12.68%").
   *  sentimentClass is intentionally fixed per metric type (a spike in
   *  Duplicate/Fake scans is bad news even though the number went up),
   *  so it can't be reused here - the pill needs to react to the actual
   *  sign of totalScansChangePercent instead, the same way changeArrowIcon
   *  already does. Use this getter (not sentimentClass) for the pill's
   *  background/text color class in the template. */
  get changeSentimentClass(): string {
    return this.totalScansChangePercent < 0 ? "az-bad" : "az-up";
  }

  statusClass(status: ScanRecord["status"]): string {
    switch (status) {
      case "Authentic":
        return "status-pill status-authentic";
      case "Suspected":
        return "status-pill status-suspected";
      case "Duplicate":
        return "status-pill status-duplicate";
      case "Invalid":
      case "Fake":
        return "status-pill status-invalid";
      case "Unverified":
        return "status-pill status-unverified";
      default:
        return "status-pill";
    }
  }

  /** Raw backend status "Invalid" displays as "Fake" everywhere in this UI. */
  statusLabel(status: ScanRecord["status"]): string {
    return status === "Invalid" ? "Fake" : status;
  }

  starsArray(rating: number | null): number[] {
    const filledCount = rating || 0;
    return Array(5)
      .fill(0)
      .map((_, i) => (i < filledCount ? 1 : 0));
  }

  /** Builds and downloads the CSV for a given set of rows. Split out of
   *  exportData() so both the "fetched every row from the server" path and
   *  the local-fallback path share one place that actually writes the
   *  file. */
  private downloadCsv(exportRows: ScanRecord[]): void {
    const headers = [
      "Seq. No.",
      "Timestamp",
      "Product Name",
      "Mobile Number",
      "User Name",
      "Status",
      "Reason",
      "Scan Count",
      "Address",
      "City",
      "State",
      "Country",
      "Location",
      "Product Rating",
      "Feedback",
    ];
    const rows = exportRows.map((row) => [
      row.codeSerial,
      row.timestamp,
      row.productName,
      row.mobileNumber,
      row.verifiedBy,
      row.status,
      row.reason,
      row.scanCount != null ? String(row.scanCount) : "",
      row.address,
      row.city,
      row.state,
      row.country,
      this.hasCoords(row) ? this.coordLabel(row) : "Location not captured",
      row.productRating != null ? String(row.productRating) : "",
      row.feedback,
    ]);
    const escapeCsv = (val: string) => `"${(val || "").replace(/"/g, '""')}"`;
    const csv = [headers, ...rows]
      .map((cols) => cols.map(escapeCsv).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `scan-data-${this.metricType}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  /** Exports every row matching the current filters/date range, not just
   *  the current page. PageSize: -1 is the API's sentinel for "return
   *  every matching row, ignore pagination" (PageNo is irrelevant in that
   *  case but reset to 1 for clarity) - preferred over passing our own
   *  locally-tracked serverTotalRecords, which could be stale by the time
   *  the user clicks export. Falls back to whatever's already loaded
   *  client-side if no server data has loaded yet, or if this dedicated
   *  export request fails. */
  exportData(): void {
    if (this.isExporting) return;

    if (!this.usingServerData || this.serverTotalRecords <= 0) {
      this.downloadCsv(
        this.filteredScanData.length
          ? this.filteredScanData
          : this.pagedScanData,
      );
      return;
    }

    const { from, to } = this.getDateRange();
    const payload = {
      CompId: this.getCompId(),
      FromDate: from,
      ToDate: to,
      PageNo: 1,
      PageSize: -1,
      ProductId: this.getSelectedProductId(),
      Status: this.getSelectedStatus(),
      Search: this.searchTerm.trim() || null,
    };

    this.isExporting = true;

    this.dashboardService.getScanIntelligenceGraph(payload).subscribe({
      next: (response: any) => {
        this.isExporting = false;
        const details = response && response.details;
        const apiRows: any[] =
          details && Array.isArray(details.data) ? details.data : [];
        const rows = apiRows.map((row) => this.mapApiRow(row));
        // A malformed/empty response here shouldn't leave the user with
        // no file at all - fall back to the current page rather than
        // silently doing nothing.
        this.downloadCsv(rows.length ? rows : this.pagedScanData);
      },
      error: (error) => {
        console.error("Export GetScanIntelligenceGraph error:", error);
        this.isExporting = false;
        this.downloadCsv(this.pagedScanData);
      },
    });
  }

  refreshData(): void {
    // Re-hits getScanIntelligenceGraph() to pull fresh data, same spin
    // feedback as the dashboard's #syncBtn (see header.component.css
    // `#syncBtn.is-syncing svg`).
    const syncBtn = document.getElementById("syncBtn");
    if (syncBtn) {
      syncBtn.classList.add("is-syncing");
      setTimeout(() => syncBtn.classList.remove("is-syncing"), 700);
    }
    this.applyAllFilters();
    this.loadReport();
  }
}
